import { useMemo, useState } from 'react'
import { AlertCircle, CheckCircle2, ChevronRight, FileSearch, ShieldAlert } from 'lucide-react'
import type { DeterministicPreview } from '../types'

type JsonRecord = Record<string, unknown>

const REVIEW_TABS = [
  ['overview', 'Overview'],
  ['visits', 'Visits'],
  ['timepoints', 'Timepoints'],
  ['procedures', 'Procedures'],
  ['forms', 'Forms'],
  ['fields', 'Fields'],
  ['laboratory', 'Laboratory'],
  ['interventions', 'Interventions'],
  ['relationships', 'Relationships'],
  ['review', 'Export Exceptions'],
] as const

type ReviewTab = typeof REVIEW_TABS[number][0]

interface CmmReviewPanelProps {
  preview: DeterministicPreview | null
  loading?: boolean
}

function asRecord(value: unknown): JsonRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : {}
}

function asArray(value: unknown): JsonRecord[] {
  return Array.isArray(value) ? value.filter((item): item is JsonRecord => Boolean(item && typeof item === 'object')) : []
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '' || value === 'NOT SPECIFIED') return 'Not specified'
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

function statusOf(item: JsonRecord): string {
  return String(item.status || item.mapping_status || (item.human_review_required ? 'HUMAN_REVIEW_REQUIRED' : 'DERIVED'))
}

function evidenceOf(item: JsonRecord): string {
  return displayValue(item.evidence || item.source || item.source_text_span)
}

function CmmStatus({ status }: { status: string }) {
  const normalized = status.toUpperCase()
  const review = normalized.includes('REVIEW') || normalized === 'PROVISIONAL'
  const approved = normalized === 'APPROVED' || normalized === 'DERIVED' || normalized === 'MAPPED' || normalized === 'READY'
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
      review ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
        approved ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
          'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
    }`}>
      {review ? <ShieldAlert className="h-3.5 w-3.5" aria-hidden={true} /> : approved ? <CheckCircle2 className="h-3.5 w-3.5" aria-hidden={true} /> : null}
      {status.replace(/_/g, ' ')}
    </span>
  )
}

function ArtifactTable({ title, items, columns }: { title: string; items: JsonRecord[]; columns: string[] }) {
  const [selected, setSelected] = useState<JsonRecord | null>(null)
  if (!items.length) {
    return <div className="panel p-6 text-sm text-gray-500 dark:text-gray-400">No {title.toLowerCase()} are available in the current CMM.</div>
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">{items.length} records</span>
      </div>
      <div className="overflow-x-auto panel">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {columns.map((column) => <th key={column} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{column}</th>)}
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {items.map((item, index) => {
              const id = displayValue(item.visit_id || item.timepoint_id || item.procedure_id || item.form_id || item.field_id || item.panel_id || item.intervention_id || item.artifact_id || index + 1)
              return (
                <tr key={`${id}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                  {columns.map((column) => {
                    const key = column.toLowerCase().replace(/ /g, '_')
                    const value = key === 'status' ? statusOf(item) : item[key]
                    return <td key={column} className="max-w-[280px] px-4 py-3 text-sm text-gray-700 dark:text-gray-200 align-top">{key === 'status' ? <CmmStatus status={String(value)} /> : <span className="break-words">{displayValue(value)}</span>}</td>
                  })}
                  <td className="px-4 py-3 text-right align-top"><button type="button" onClick={() => setSelected(item)} className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400" title="Open evidence details">Details <ChevronRight className="h-4 w-4" aria-hidden={true} /></button></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {selected && <EvidencePanel item={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}

function EvidencePanel({ item, onClose }: { item: JsonRecord; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation" onClick={onClose}>
      <div className="panel max-h-[90vh] w-full max-w-3xl overflow-y-auto border-primary-200 dark:border-primary-900 p-5 space-y-4 shadow-xl" role="dialog" aria-modal="true" aria-label="CMM evidence details" onClick={(event) => event.stopPropagation()}>
      <div className="flex items-start justify-between gap-4">
        <div><h4 className="font-semibold text-gray-900 dark:text-white">Artifact details</h4><p className="text-xs text-gray-500 dark:text-gray-400">Source values are read-only. Unresolved items are carried into the automated export exception report.</p></div>
        <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white">Close</button>
      </div>
      <div className="grid gap-3 sm:grid-cols-3 text-sm">
        <div><div className="text-xs text-gray-500">Status</div><CmmStatus status={statusOf(item)} /></div>
        <div><div className="text-xs text-gray-500">Confidence</div><div className="text-gray-900 dark:text-white">{displayValue(item.confidence)}</div></div>
        <div><div className="text-xs text-gray-500">Identifier</div><div className="break-all text-gray-900 dark:text-white">{displayValue(item.artifact_id || item.visit_id || item.procedure_id || item.form_id || item.field_id || item.panel_id || item.intervention_id)}</div></div>
      </div>
      <div><div className="text-xs font-medium text-gray-500">Evidence</div><pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-3 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-200">{evidenceOf(item)}</pre></div>
      {item.reason !== undefined && item.reason !== null && <div className="text-sm text-amber-800 dark:text-amber-300"><strong>Reason:</strong> {displayValue(item.reason)}</div>}
      {item.missing_source !== undefined && item.missing_source !== null && <div className="text-sm text-amber-800 dark:text-amber-300"><strong>Missing source:</strong> {displayValue(item.missing_source)}</div>}
      {item.assumptions !== undefined && item.assumptions !== null && <div className="text-sm text-gray-600 dark:text-gray-300"><strong>Assumptions:</strong> {displayValue(item.assumptions)}</div>}
      <details><summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-200">Original CMM object</summary><pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-3 text-xs dark:bg-gray-900">{JSON.stringify(item, null, 2)}</pre></details>
      </div>
    </div>
  )
}

export default function CmmReviewPanel({ preview, loading = false }: CmmReviewPanelProps) {
  const [tab, setTab] = useState<ReviewTab>('overview')
  const packageData = asRecord(preview?.clin_spark_study_package)
  const design = asRecord(packageData.study_build_design)
  const cmm = asRecord(design.CMM || packageData.CMM)
  const missing = !preview || !Object.keys(cmm).length
  const artifactGroups = useMemo(() => ({
    visits: asArray(cmm.visit_dictionary),
    timepoints: asArray(cmm.timepoint_dictionary),
    procedures: asArray(cmm.procedure_dictionary),
    forms: asArray(cmm.form_dictionary),
    fields: asArray(cmm.field_dictionary),
    laboratory: asArray(cmm.laboratory_dictionary),
    interventions: asArray(cmm.intervention_dictionary),
    relationships: [
      ...asArray(cmm.visit_timepoint_mapping),
      ...asArray(cmm.timepoint_procedure_mapping),
      ...asArray(cmm.procedure_form_mapping),
      ...asArray(cmm.lab_panel_procedure_mapping),
      ...asArray(cmm.intervention_visit_mapping),
    ],
    review: asArray(cmm.human_review_registry),
  }), [cmm])

  if (loading) return <div className="panel p-6 text-sm text-gray-500">Loading CMM review...</div>
  if (missing) return <div className="panel p-6 space-y-2"><div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white"><AlertCircle className="h-5 w-5 text-amber-500" aria-hidden={true} />No CMM available</div><p className="text-sm text-gray-600 dark:text-gray-300">Complete deterministic protocol preparation before reviewing build artifacts.</p></div>

  const summary = asRecord(cmm.build_readiness_summary)
  const gaps = asArray(cmm.build_gap_registry)
  const counts = Object.fromEntries(Object.entries(artifactGroups).map(([key, values]) => [key, values.length]))
  return (
    <div className="space-y-4">
      <div className="panel p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div><div className="flex items-center gap-2"><FileSearch className="h-5 w-5 text-primary-600" aria-hidden={true} /><h2 className="font-semibold text-gray-900 dark:text-white">CMM Review</h2></div><p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Inspect the exact deterministic build artifacts before automated ClinSpark export.</p></div>
          <div className="flex items-center gap-3 text-sm"><CmmStatus status={String(summary.status || (gaps.length ? 'HUMAN_REVIEW_REQUIRED' : 'DERIVED'))} /><span className="text-gray-600 dark:text-gray-300">Readiness {displayValue(summary.score)}%</span></div>
        </div>
        <nav className="mt-4 flex gap-2 overflow-x-auto" aria-label="CMM review sections">{REVIEW_TABS.map(([key, label]) => <button key={key} type="button" onClick={() => setTab(key)} className={`whitespace-nowrap rounded px-3 py-1.5 text-sm ${tab === key ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'}`}>{label}{key !== 'overview' && <span className="ml-1 text-xs opacity-70">{counts[key as keyof typeof counts] ?? 0}</span>}</button>)}</nav>
      </div>
      {tab === 'overview' && <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{[['Visits', counts.visits], ['Procedures', counts.procedures], ['Forms', counts.forms], ['Review items', counts.review]].map(([label, value]) => <div key={String(label)} className="panel p-4"><div className="text-xs text-gray-500 dark:text-gray-400">{label}</div><div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{value}</div></div>)}<div className="panel p-4 sm:col-span-2 lg:col-span-4"><div className="text-xs text-gray-500 dark:text-gray-400">Build gaps</div><div className="mt-2 text-sm text-amber-800 dark:text-amber-300">{gaps.length ? gaps.map((gap, index) => <div key={index}>{displayValue(gap.component || gap.reason)}</div>) : 'No build gaps reported.'}</div></div></div>}
      {tab === 'visits' && <ArtifactTable title="Visits" items={artifactGroups.visits} columns={['visit_name', 'study_day', 'period', 'visit_type', 'status']} />}
      {tab === 'timepoints' && <ArtifactTable title="Timepoints" items={artifactGroups.timepoints} columns={['timepoint_name', 'visit_id', 'relative_time_value', 'relative_time_unit', 'status']} />}
      {tab === 'procedures' && <ArtifactTable title="Procedures" items={artifactGroups.procedures} columns={['procedure_name', 'category', 'visit_id', 'timepoint', 'status']} />}
      {tab === 'forms' && <ArtifactTable title="Forms" items={artifactGroups.forms} columns={['form_name', 'domain', 'repeatable', 'status']} />}
      {tab === 'fields' && <ArtifactTable title="Fields" items={artifactGroups.fields} columns={['field_name', 'field_label', 'field_type', 'form_id', 'status']} />}
      {tab === 'laboratory' && <ArtifactTable title="Laboratory panels" items={artifactGroups.laboratory} columns={['panel_name', 'specimen_type', 'analytes', 'status']} />}
      {tab === 'interventions' && <ArtifactTable title="Interventions" items={artifactGroups.interventions} columns={['product_name', 'arm_type', 'route', 'dose_level', 'dose_unit', 'status']} />}
      {tab === 'relationships' && <ArtifactTable title="Relationships" items={artifactGroups.relationships} columns={['visit_id', 'timepoint_id', 'procedure_id', 'form_id', 'status']} />}
      {tab === 'review' && <ArtifactTable title="Human review queue" items={artifactGroups.review} columns={['component', 'id', 'reason', 'action', 'status']} />}
    </div>
  )
}

export function OdmStandardizationPanel({ preview, loading = false }: CmmReviewPanelProps) {
  const packageData = asRecord(preview?.clin_spark_study_package)
  const referenceStandardization = asRecord(packageData.reference_standardization)
  const referenceSummary = asRecord(referenceStandardization.catalog_summary)
  const items = asArray(referenceStandardization.form_matches)

  if (loading) return <div className="panel p-6 text-sm text-gray-500">Loading ODM standardization...</div>
  if (!preview) return <div className="panel p-6 text-sm text-gray-600 dark:text-gray-300">Complete deterministic protocol preparation before reviewing ODM standardization.</div>
  if (!items.length) {
    return <div className="panel space-y-2 p-6 text-sm text-gray-600 dark:text-gray-300"><h2 className="font-semibold text-gray-900 dark:text-white">No ODM standardization rows</h2>{Number(referenceSummary.forms || 0) > 0 ? <p>The processed ODM catalog contains {displayValue(referenceSummary.forms)} forms, but no protocol form produced a standardization row. Refresh the preview after processing the protocol.</p> : <p>No processed ODM references are associated with this user and study. Upload and process an ODM reference, then refresh this study.</p>}<p className="text-xs text-gray-500 dark:text-gray-400">Reference enrichment is review-only. It does not rename or overwrite protocol-derived forms automatically.</p></div>
  }
  return <ArtifactTable title="ODM standardization candidates" items={items} columns={['original_form_name', 'standardized_form_name', 'selected_reference_form_id', 'confidence', 'status']} />
}
